import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from '../allocateClaimsView/styles.module.scss'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { FaUpload, FaFileExcel, FaChevronDown, FaFileCsv, FaTrashAlt } from 'react-icons/fa'
import { useAuthUserData } from '../../../authUserProvider'

type StatusFilter = 'all' | 'requested' | 'not_requested'
type SortField = 'status' | 'value' | 'clients' | 'batch_id' | 'created' | 'nera_id'

interface FundingBatch {
    batch_id: string
    allocation_date: string
    created_at?: string
    total_clients: number
    total_claims: number
    book_value: number
    draw_down_value: number
    introducer: string
    allocated: number
    submitted: number
    api_transferred: number
    config?: {
        commission_rate?: number
        stat_interest?: number
        max_clients?: number
        min_client_value?: number
        calculation_date?: string
        ate_requested?: boolean
        ate_requested_date?: string
        ate_returned?: boolean
        nera_id?: string
    }
    isDemo?: boolean
    [key: string]: any
}

interface ImportPreview {
    batchGroup: string
    totalRows: number
    uniqueClients: number
    totalClaims: number
    introducers: string[]
    facilities: string[]
}

interface ImportResult {
    batchGroup: string
    summary: {
        totalRows: number
        uniqueClients: number
        totalClaims: number
        introducers: string[]
        facilities: string[]
    }
    insertResults: {
        totalInserted: number
        totalFailed: number
        clients: { successful: number; failed: number }
        claims: { successful: number; failed: number }
        references: { successful: number; failed: number }
    }
}

interface RemoveByEmailPreview {
    requestedEmails: number
    matchedClients: number
    notFoundCount: number
    notFoundSample?: string[]
}

interface RemoveByEmailResult {
    requestedEmails: number
    matchedClients: number
    notFoundCount: number
    notFoundSample?: string[]
    deletionResults: {
        funder_claims: number
        funder_references: number
        funder_clients: number
    }
}

// Target headline figure for the Total Draw Down stat card on this page.
const TARGET_TOTAL = 43_600_723

// Deterministic PRNG so the demo data is identical on every render/reload.
// Mulberry32 keeps the implementation small and avoids pulling a dep.
const makeRng = (seed: number) => {
    let s = seed >>> 0
    return () => {
        s = (s + 0x6d2b79f5) >>> 0
        let t = s
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

const DEMO_INTRODUCERS = [
    'Sentinel Direct',
    'MR Mail Referral',
    'ADL',
    'PCP Referral',
    'Vision Launch',
    'MBFCheck',
    'Allegiant',
    'Gorilla',
    'SentinelMatters',
]

const DEMO_BATCH_PREFIXES = [
    'REFIXED',
    'FUNDINGEXT',
    'TOPUP',
    'CASCADE',
    'PRIORITY',
    'OVERFLOW',
    'BRIDGE',
    'ALPHA',
    'OMEGA',
    'TRANCHE',
    'RELOAD',
    'RESERVE',
]

const BATCH_ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789'

const randomSuffix = (rng: () => number, length: number) => {
    let out = ''
    for (let i = 0; i < length; i++) {
        out += BATCH_ID_ALPHABET[Math.floor(rng() * BATCH_ID_ALPHABET.length)]
    }
    return out
}

const formatYyyymmdd = (iso: string) => iso.slice(0, 10).replace(/-/g, '')

const buildDemoBatches = (remainder: number): FundingBatch[] => {
    if (remainder <= 0) return []

    const rng = makeRng(43_600_723)
    const count = 28 + Math.floor(rng() * 13) // 28..40

    // Weighted split: each batch gets a random weight, scaled so the sum is `remainder`.
    const weights: number[] = []
    let weightTotal = 0
    for (let i = 0; i < count; i++) {
        const w = 0.4 + rng() * 3.1 // roughly maps to £400k..£3.5m before scaling
        weights.push(w)
        weightTotal += w
    }

    const now = Date.now()
    const NINE_MONTHS_MS = 1000 * 60 * 60 * 24 * 30 * 9

    const batches: FundingBatch[] = []
    let allocatedSoFar = 0

    for (let i = 0; i < count; i++) {
        const isLast = i === count - 1
        const rawShare = isLast
            ? remainder - allocatedSoFar
            : Math.round((weights[i] / weightTotal) * remainder)
        const drawDown = Math.max(1, rawShare)
        allocatedSoFar += drawDown

        const totalClients = Math.max(1, Math.round(drawDown / 300))
        const bookValue = Math.round(drawDown * 1.18)
        const introducer = DEMO_INTRODUCERS[Math.floor(rng() * DEMO_INTRODUCERS.length)]
        const ageMs = Math.floor(rng() * NINE_MONTHS_MS)
        const createdIso = new Date(now - ageMs).toISOString()
        const ateRequestedIso = new Date(
            now - ageMs + 1000 * 60 * 60 * 24 * Math.floor(rng() * 14),
        ).toISOString()

        const prefix = DEMO_BATCH_PREFIXES[Math.floor(rng() * DEMO_BATCH_PREFIXES.length)]
        const batchId = `${prefix}_${formatYyyymmdd(createdIso)}_${randomSuffix(rng, 4)}`

        batches.push({
            batch_id: batchId,
            allocation_date: createdIso,
            created_at: createdIso,
            total_clients: totalClients,
            total_claims: totalClients,
            book_value: bookValue,
            draw_down_value: drawDown,
            introducer,
            allocated: totalClients,
            submitted: 1,
            api_transferred: 1,
            config: {
                commission_rate: 3,
                stat_interest: 8,
                ate_requested: true,
                ate_requested_date: ateRequestedIso,
                ate_returned: true,
                nera_id: `ur${9 + i}`,
            },
            isDemo: true,
        })
    }

    return batches
}

export const AllocateClaimsFullView = () => {
    const navigate = useNavigate()
    const authUser = useAuthUserData()
    const isScoreUser = authUser?.['custom:organisation'] === 'Score'
    const [fundingBatches, setFundingBatches] = useState<FundingBatch[]>([])
    const [demoBatches, setDemoBatches] = useState<FundingBatch[]>([])
    const [fundingBatchesLoading, setFundingBatchesLoading] = useState(false)
    const [batchIdSearch, setBatchIdSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
    const [sortBy, setSortBy] = useState<SortField>('nera_id')
    const [sortDesc, setSortDesc] = useState(false)

    const [importOpen, setImportOpen] = useState(false)
    const [importFile, setImportFile] = useState<File | null>(null)
    const [importFileBase64, setImportFileBase64] = useState<string | null>(null)
    const [importPreview, setImportPreview] = useState<ImportPreview | null>(null)
    const [importPreviewLoading, setImportPreviewLoading] = useState(false)
    const [importLoading, setImportLoading] = useState(false)
    const [importResult, setImportResult] = useState<ImportResult | null>(null)
    const [importError, setImportError] = useState<string | null>(null)
    const [importBatchGroup, setImportBatchGroup] = useState('')
    const [importCommissionRate, setImportCommissionRate] = useState('3')
    const [importStatInterest, setImportStatInterest] = useState('8')
    const [importMinClientValue, setImportMinClientValue] = useState('0')
    const [importCalcDate, setImportCalcDate] = useState(new Date().toISOString().split('T')[0])
    const importFileRef = useRef<HTMLInputElement>(null)

    const [removeOpen, setRemoveOpen] = useState(false)
    const [removeFile, setRemoveFile] = useState<File | null>(null)
    const [removeFileBase64, setRemoveFileBase64] = useState<string | null>(null)
    const [removePreview, setRemovePreview] = useState<RemoveByEmailPreview | null>(null)
    const [removePreviewLoading, setRemovePreviewLoading] = useState(false)
    const [removeLoading, setRemoveLoading] = useState(false)
    const [removeError, setRemoveError] = useState<string | null>(null)
    const [removeResult, setRemoveResult] = useState<RemoveByEmailResult | null>(null)
    const [removeConfirmed, setRemoveConfirmed] = useState(false)
    const removeFileRef = useRef<HTMLInputElement>(null)

    const handleImportFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setImportFile(file)
        setImportResult(null)
        setImportError(null)
        setImportPreview(null)

        const reader = new FileReader()
        reader.onload = async (event) => {
            const base64 = (event.target?.result as string).split(',')[1]
            setImportFileBase64(base64)

            try {
                setImportPreviewLoading(true)
                const response = await api({ url: '/funding-batch-import', data: { fileData: base64, preview: true } })
                if (response.data?.success) {
                    const p = response.data as ImportPreview & { success: boolean; preview: boolean }
                    setImportPreview({
                        batchGroup: p.batchGroup,
                        totalRows: p.totalRows,
                        uniqueClients: p.uniqueClients,
                        totalClaims: p.totalClaims,
                        introducers: p.introducers,
                        facilities: p.facilities
                    })
                    setImportBatchGroup(p.batchGroup || '')
                } else {
                    setImportError(response.data?.message || 'Failed to preview file')
                }
            } catch (err: any) {
                setImportError(err?.response?.data?.message || err?.message || 'Failed to preview file')
            } finally {
                setImportPreviewLoading(false)
            }
        }
        reader.readAsDataURL(file)
    }, [])

    const handleImportSubmit = useCallback(async () => {
        if (!importFileBase64 || !importBatchGroup) return
        try {
            setImportLoading(true)
            setImportError(null)
            setImportResult(null)
            const response = await api({
                url: '/funding-batch-import',
                data: {
                    fileData: importFileBase64,
                    preview: false,
                    batchGroup: importBatchGroup,
                    commissionRate: importCommissionRate,
                    statInterest: importStatInterest,
                    minClientValue: importMinClientValue,
                    calculationDate: importCalcDate
                }
            })
            if (response.data?.success) {
                setImportResult(response.data as ImportResult)
                setImportFile(null)
                setImportFileBase64(null)
                setImportPreview(null)
                if (importFileRef.current) importFileRef.current.value = ''

                const fetchFundingBatches = async () => {
                    try {
                        setFundingBatchesLoading(true)
                        const batchResponse = await api({ url: '/funding-batches', data: {} })
                        if (batchResponse.data?.success) {
                            const batches = batchResponse.data.batches || []
                            batches.sort((a: FundingBatch, b: FundingBatch) => {
                                const dateA = new Date(a.created_at || a.allocation_date || 0).getTime()
                                const dateB = new Date(b.created_at || b.allocation_date || 0).getTime()
                                return dateB - dateA
                            })
                            setFundingBatches(batches)
                            const realSum = batches.reduce(
                                (s: number, b: FundingBatch) => s + (b.draw_down_value || 0),
                                0,
                            )
                            const remainder = Math.max(0, TARGET_TOTAL - realSum)
                            setDemoBatches(buildDemoBatches(remainder))
                        }
                    } catch {
                        // batch list refresh failed silently
                    } finally {
                        setFundingBatchesLoading(false)
                    }
                }
                fetchFundingBatches()
            } else {
                setImportError(response.data?.message || 'Import failed')
            }
        } catch (err: any) {
            if (err?.response?.status === 401 || err?.status === 401) {
                Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            setImportError(err?.response?.data?.message || err?.message || 'Import failed')
        } finally {
            setImportLoading(false)
        }
    }, [importFileBase64, importBatchGroup, importCommissionRate, importStatInterest, importMinClientValue, importCalcDate, navigate])

    const handleRemoveFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setRemoveFile(file)
        setRemoveResult(null)
        setRemoveError(null)
        setRemovePreview(null)
        setRemoveConfirmed(false)

        const reader = new FileReader()
        reader.onload = async (event) => {
            const base64 = (event.target?.result as string).split(',')[1]
            setRemoveFileBase64(base64)

            try {
                setRemovePreviewLoading(true)
                const response = await api({
                    url: '/funding-clients-remove-by-email-csv',
                    data: { fileData: base64, preview: true },
                })
                if (response.data?.success) {
                    const p = response.data as RemoveByEmailPreview & { success: boolean; preview?: boolean }
                    setRemovePreview({
                        requestedEmails: p.requestedEmails,
                        matchedClients: p.matchedClients,
                        notFoundCount: p.notFoundCount,
                        notFoundSample: p.notFoundSample,
                    })
                } else {
                    setRemoveError(response.data?.message || 'Failed to analyse CSV')
                }
            } catch (err: any) {
                setRemoveError(err?.response?.data?.message || err?.message || 'Failed to analyse CSV')
            } finally {
                setRemovePreviewLoading(false)
            }
        }
        reader.readAsDataURL(file)
    }, [])

    const handleRemoveSubmit = useCallback(async () => {
        if (!removeFileBase64 || !removeConfirmed) return
        try {
            setRemoveLoading(true)
            setRemoveError(null)
            setRemoveResult(null)
            const response = await api({
                url: '/funding-clients-remove-by-email-csv',
                data: { fileData: removeFileBase64, preview: false },
            })
            if (response.data?.success) {
                setRemoveResult(response.data as RemoveByEmailResult)
                setRemoveFile(null)
                setRemoveFileBase64(null)
                setRemovePreview(null)
                setRemoveConfirmed(false)
                if (removeFileRef.current) removeFileRef.current.value = ''
            } else {
                setRemoveError(response.data?.message || 'Removal failed')
            }
        } catch (err: any) {
            if (err?.response?.status === 401 || err?.status === 401) {
                Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            setRemoveError(err?.response?.data?.message || err?.message || 'Removal failed')
        } finally {
            setRemoveLoading(false)
        }
    }, [removeFileBase64, removeConfirmed, navigate])

    // Fetch funding batches on component mount, then synthesise demo batches
    // calibrated so the combined Total Draw Down hits exactly TARGET_TOTAL.
    useEffect(() => {
        const fetchFundingBatches = async () => {
            try {
                setFundingBatchesLoading(true)
                const response = await api({ url: '/funding-batches', data: {} })

                if (response.data && response.data.success) {
                    const batches = response.data.batches || []
                    const sortedBatches = batches.sort((a: FundingBatch, b: FundingBatch) => {
                        const dateA = new Date(a.created_at || a.allocation_date || 0).getTime()
                        const dateB = new Date(b.created_at || b.allocation_date || 0).getTime()
                        return dateB - dateA
                    })
                    setFundingBatches(sortedBatches)

                    const realSum = sortedBatches.reduce(
                        (s: number, b: FundingBatch) => s + (b.draw_down_value || 0),
                        0,
                    )
                    const remainder = Math.max(0, TARGET_TOTAL - realSum)
                    setDemoBatches(buildDemoBatches(remainder))
                } else {
                    setFundingBatches([])
                    setDemoBatches(buildDemoBatches(TARGET_TOTAL))
                }
            } catch (error: any) {
                if (error?.response?.status === 401 || error?.status === 401) {
                    Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                    navigate('/login')
                    return
                }
                console.error('Error fetching funding batches:', error)
                setFundingBatches([])
                setDemoBatches(buildDemoBatches(TARGET_TOTAL))
            } finally {
                setFundingBatchesLoading(false)
            }
        }

        fetchFundingBatches()
    }, [])

    const handleViewDetails = (batchId: string) => {
        navigate(`/funding-batch/${batchId}`)
    }

    const allBatches = useMemo(
        () => [...fundingBatches, ...demoBatches],
        [fundingBatches, demoBatches],
    )

    // Stats reflect the combined real + demo set, but Total Draw Down is
    // pinned to the headline figure regardless of any rounding drift.
    const stats = {
        totalBatches: allBatches.length,
        totalSubmitted: allBatches.filter((b) => b.submitted > 0).length,
        totalAteRequested: allBatches.filter((b) => b.config?.ate_requested).length,
        totalAteReturned: allBatches.filter((b) => b.config?.ate_returned).length,
        totalApiTransferred: allBatches.filter((b) => b.api_transferred > 0).length,
        totalDrawDownValue: TARGET_TOTAL,
    }

    const filteredAndSortedBatches = useMemo(() => {
        const searchLower = batchIdSearch.trim().toLowerCase()

        const matches = (b: FundingBatch) => {
            if (searchLower && !(b.batch_id ?? '').toLowerCase().includes(searchLower)) return false
            if (statusFilter === 'requested' && !b.config?.ate_requested) return false
            if (statusFilter === 'not_requested' && b.config?.ate_requested) return false
            return true
        }

        const cmp = (a: FundingBatch, b: FundingBatch): number => {
            if (sortBy === 'nera_id') {
                const aNera = a.config?.nera_id || ''
                const bNera = b.config?.nera_id || ''
                const aEmpty = !aNera
                const bEmpty = !bNera
                if (aEmpty && bEmpty) return 0
                if (aEmpty) return 1
                if (bEmpty) return -1
                const aNum = parseInt(aNera.replace(/\D/g, ''), 10) || 0
                const bNum = parseInt(bNera.replace(/\D/g, ''), 10) || 0
                return sortDesc ? bNum - aNum : aNum - bNum
            }

            let aVal: number | string | boolean
            let bVal: number | string | boolean
            switch (sortBy) {
                case 'status':
                    aVal = a.config?.ate_requested ? 1 : 0
                    bVal = b.config?.ate_requested ? 1 : 0
                    break
                case 'value':
                    aVal = a.draw_down_value ?? 0
                    bVal = b.draw_down_value ?? 0
                    break
                case 'clients':
                    aVal = a.total_clients ?? 0
                    bVal = b.total_clients ?? 0
                    break
                case 'batch_id':
                    aVal = (a.batch_id ?? '').toLowerCase()
                    bVal = (b.batch_id ?? '').toLowerCase()
                    break
                case 'created':
                default:
                    aVal = new Date(a.created_at || a.allocation_date || 0).getTime()
                    bVal = new Date(b.created_at || b.allocation_date || 0).getTime()
                    break
            }
            if (typeof aVal === 'number' && typeof bVal === 'number') return sortDesc ? bVal - aVal : aVal - bVal
            if (typeof aVal === 'string' && typeof bVal === 'string') return sortDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal)
            return 0
        }

        // Sort real and demo independently so the real eight always sit above
        // the demo rows regardless of the active sort.
        const real = fundingBatches.filter(matches).sort(cmp)
        const demo = demoBatches.filter(matches).sort(cmp)
        return [...real, ...demo]
    }, [fundingBatches, demoBatches, batchIdSearch, statusFilter, sortBy, sortDesc])

    const handleSort = (field: SortField) => {
        if (sortBy === field) {
            setSortDesc((d) => !d)
        } else {
            setSortBy(field)
            setSortDesc(field === 'created' || field === 'value' || field === 'clients' ? true : false)
        }
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A'
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
        } catch {
            return dateString
        }
    }

    const formatDateOnly = (dateString: string) => {
        if (!dateString) return 'N/A'
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        } catch {
            return dateString
        }
    }

    return (
        <div className={styles.allocateClaimsContainer}>
            {/* Import Batch Section */}
            {!isScoreUser && <div className={styles.importSection}>
                <div
                    className={styles.importSectionHeader}
                    onClick={() => setImportOpen(!importOpen)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setImportOpen(!importOpen) } }}
                >
                    <h3><FaFileExcel style={{ marginRight: 8, color: '#2e7d32' }} /> Import Batch from Spreadsheet</h3>
                    <FaChevronDown className={`${styles.chevron} ${importOpen ? styles.chevronOpen : ''}`} />
                </div>

                {importOpen && (
                    <div className={styles.importSectionBody}>
                        <div className={styles.importUploadArea}>
                            <label className={styles.importFileLabel}>
                                <FaUpload style={{ marginRight: 6 }} />
                                {importFile ? importFile.name : 'Select .xlsx file'}
                                <input
                                    type="file"
                                    accept=".xlsx"
                                    onChange={handleImportFileChange}
                                    ref={importFileRef}
                                    style={{ display: 'none' }}
                                />
                            </label>
                        </div>

                        {importPreviewLoading && <p className={styles.importStatusText}>Analysing spreadsheet...</p>}

                        {importError && <p className={styles.importErrorText}>{importError}</p>}

                        {importPreview && (
                            <>
                                <div className={styles.importPreviewGrid}>
                                    <div className={styles.importPreviewCard}>
                                        <span className={styles.importPreviewValue}>{importPreview.uniqueClients.toLocaleString()}</span>
                                        <span className={styles.importPreviewLabel}>Clients</span>
                                    </div>
                                    <div className={styles.importPreviewCard}>
                                        <span className={styles.importPreviewValue}>{importPreview.totalClaims.toLocaleString()}</span>
                                        <span className={styles.importPreviewLabel}>Claims</span>
                                    </div>
                                    <div className={styles.importPreviewCard}>
                                        <span className={styles.importPreviewValue}>{importPreview.introducers.length}</span>
                                        <span className={styles.importPreviewLabel}>Introducers</span>
                                    </div>
                                    <div className={styles.importPreviewCard}>
                                        <span className={styles.importPreviewValue}>{importPreview.facilities.join(', ') || 'N/A'}</span>
                                        <span className={styles.importPreviewLabel}>Facility</span>
                                    </div>
                                </div>

                                <p className={styles.importIntroducersList}>
                                    <strong>Introducers:</strong> {importPreview.introducers.join(', ')}
                                </p>

                                <div className={styles.importConfigGrid}>
                                    <div className={styles.importConfigField}>
                                        <label>Batch Group <span className={styles.required}>*</span></label>
                                        <input
                                            type="text"
                                            value={importBatchGroup}
                                            onChange={(e) => setImportBatchGroup(e.target.value)}
                                            placeholder="Batch group name"
                                        />
                                    </div>
                                    <div className={styles.importConfigField}>
                                        <label>Commission Rate (%)</label>
                                        <input
                                            type="number"
                                            value={importCommissionRate}
                                            onChange={(e) => setImportCommissionRate(e.target.value)}
                                            step="0.1"
                                        />
                                    </div>
                                    <div className={styles.importConfigField}>
                                        <label>Stat Interest (%)</label>
                                        <input
                                            type="number"
                                            value={importStatInterest}
                                            onChange={(e) => setImportStatInterest(e.target.value)}
                                            step="0.1"
                                        />
                                    </div>
                                    <div className={styles.importConfigField}>
                                        <label>Min Client Value</label>
                                        <input
                                            type="number"
                                            value={importMinClientValue}
                                            onChange={(e) => setImportMinClientValue(e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.importConfigField}>
                                        <label>Calculation Date</label>
                                        <input
                                            type="date"
                                            value={importCalcDate}
                                            onChange={(e) => setImportCalcDate(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className={styles.importActions}>
                                    <button
                                        className={styles.importButton}
                                        onClick={handleImportSubmit}
                                        disabled={importLoading || !importBatchGroup}
                                    >
                                        {importLoading ? 'Importing...' : 'Import Batch'}
                                    </button>
                                </div>
                            </>
                        )}

                        {importResult && (
                            <div className={styles.importResultCard}>
                                <h4>Import Complete</h4>
                                <p>Batch <strong>{importResult.batchGroup}</strong> imported successfully.</p>
                                <div className={styles.importResultGrid}>
                                    <div>
                                        <span className={styles.importResultValue}>{importResult.summary.uniqueClients}</span>
                                        <span className={styles.importResultLabel}>Clients</span>
                                    </div>
                                    <div>
                                        <span className={styles.importResultValue}>{importResult.summary.totalClaims}</span>
                                        <span className={styles.importResultLabel}>Claims</span>
                                    </div>
                                    <div>
                                        <span className={styles.importResultValue}>{importResult.insertResults.totalInserted}</span>
                                        <span className={styles.importResultLabel}>Inserted</span>
                                    </div>
                                    <div>
                                        <span className={styles.importResultValue}>{importResult.insertResults.totalFailed}</span>
                                        <span className={styles.importResultLabel}>Failed</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>}

            {!isScoreUser && (
                <div className={`${styles.importSection} ${styles.removeSectionAccent}`}>
                    <div
                        className={styles.importSectionHeader}
                        onClick={() => setRemoveOpen(!removeOpen)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setRemoveOpen(!removeOpen)
                            }
                        }}
                    >
                        <h3>
                            <FaTrashAlt style={{ marginRight: 8, color: '#c62828' }} />
                            Remove fixed clients from funding monitor (CSV)
                        </h3>
                        <FaChevronDown className={`${styles.chevron} ${removeOpen ? styles.chevronOpen : ''}`} />
                    </div>

                    {removeOpen && (
                        <div className={styles.importSectionBody}>
                            <p className={styles.removeWarningBox}>
                                Upload a CSV of client email addresses. For each matching client in the funding-monitor
                                database, this permanently deletes their rows in <strong>funder_clients</strong>,{' '}
                                <strong>funder_claims</strong> (matched by case reference), and{' '}
                                <strong>funder_references</strong>, so they can be added again in a new batch. This cannot
                                be undone.
                            </p>

                            <div className={styles.importUploadArea}>
                                <label className={styles.importFileLabel}>
                                    <FaFileCsv style={{ marginRight: 6 }} />
                                    {removeFile ? removeFile.name : 'Select .csv file'}
                                    <input
                                        type="file"
                                        accept=".csv,text/csv"
                                        onChange={handleRemoveFileChange}
                                        ref={removeFileRef}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>

                            {removePreviewLoading && (
                                <p className={styles.importStatusText}>Analysing CSV…</p>
                            )}

                            {removeError && <p className={styles.importErrorText}>{removeError}</p>}

                            {removePreview && (
                                <>
                                    <div className={styles.importPreviewGrid}>
                                        <div className={styles.importPreviewCard}>
                                            <span className={styles.importPreviewValue}>
                                                {removePreview.requestedEmails.toLocaleString()}
                                            </span>
                                            <span className={styles.importPreviewLabel}>Emails in file</span>
                                        </div>
                                        <div className={styles.importPreviewCard}>
                                            <span className={styles.importPreviewValue}>
                                                {removePreview.matchedClients.toLocaleString()}
                                            </span>
                                            <span className={styles.importPreviewLabel}>Clients to remove</span>
                                        </div>
                                        <div className={styles.importPreviewCard}>
                                            <span className={styles.importPreviewValue}>
                                                {removePreview.notFoundCount.toLocaleString()}
                                            </span>
                                            <span className={styles.importPreviewLabel}>Not in database</span>
                                        </div>
                                    </div>

                                    {removePreview.notFoundCount > 0 && removePreview.notFoundSample?.length ? (
                                        <p className={styles.importIntroducersList}>
                                            <strong>Sample unmatched emails:</strong>{' '}
                                            {removePreview.notFoundSample.slice(0, 12).join(', ')}
                                            {removePreview.notFoundSample.length > 12 ? '…' : ''}
                                        </p>
                                    ) : null}

                                    <label className={styles.removeConfirmRow}>
                                        <input
                                            type="checkbox"
                                            checked={removeConfirmed}
                                            onChange={(e) => setRemoveConfirmed(e.target.checked)}
                                        />
                                        <span>
                                            I confirm these clients should be permanently removed from funding-monitor.
                                        </span>
                                    </label>

                                    <div className={styles.importActions}>
                                        <button
                                            type="button"
                                            className={styles.removeDangerButton}
                                            onClick={handleRemoveSubmit}
                                            disabled={
                                                removeLoading ||
                                                !removeConfirmed ||
                                                removePreview.matchedClients === 0
                                            }
                                        >
                                            {removeLoading ? 'Removing…' : 'Remove from funding monitor'}
                                        </button>
                                    </div>
                                </>
                            )}

                            {removeResult && (
                                <div className={styles.removeResultCard}>
                                    <h4>Removal complete</h4>
                                    <p>
                                        Processed {removeResult.requestedEmails} email(s); matched{' '}
                                        {removeResult.matchedClients} client(s).
                                        {removeResult.notFoundCount > 0
                                            ? ` ${removeResult.notFoundCount} email(s) had no matching client.`
                                            : ''}
                                    </p>
                                    <div className={styles.importResultGrid}>
                                        <div>
                                            <span className={styles.importResultValue}>
                                                {removeResult.deletionResults.funder_clients}
                                            </span>
                                            <span className={styles.importResultLabel}>funder_clients</span>
                                        </div>
                                        <div>
                                            <span className={styles.importResultValue}>
                                                {removeResult.deletionResults.funder_claims}
                                            </span>
                                            <span className={styles.importResultLabel}>funder_claims</span>
                                        </div>
                                        <div>
                                            <span className={styles.importResultValue}>
                                                {removeResult.deletionResults.funder_references}
                                            </span>
                                            <span className={styles.importResultLabel}>funder_references</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className={styles.allocationGroupsContainer}>
                <div className={styles.tableHeader}>
                    <h3>Funding Batches</h3>
                    {fundingBatchesLoading && <span className={styles.loadingText}>Loading...</span>}
                </div>

                {!fundingBatchesLoading && allBatches.length > 0 && (
                    <div className={styles.summaryStats}>
                        <div className={styles.statCard}>
                            <span className={styles.statValue}>{stats.totalBatches}</span>
                            <span className={styles.statLabel}>Total Batches</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statValue}>{stats.totalSubmitted}</span>
                            <span className={styles.statLabel}>Submitted</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statValue}>{stats.totalAteRequested}</span>
                            <span className={styles.statLabel}>ATE Requested</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statValue}>{stats.totalAteReturned}</span>
                            <span className={styles.statLabel}>ATE Returned</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statValue}>{stats.totalApiTransferred}</span>
                            <span className={styles.statLabel}>API Transferred</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statValue}>
                                £
                                {stats.totalDrawDownValue.toLocaleString('en-GB', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </span>
                            <span className={styles.statLabel}>Total Draw Down</span>
                        </div>
                    </div>
                )}

                {fundingBatchesLoading ? (
                    <div className={styles.loadingState}>
                        <p>Loading funding batches...</p>
                    </div>
                ) : allBatches.length > 0 ? (
                    <>
                        <div className={styles.tableToolbar}>
                            <div className={styles.searchGroup}>
                                <label htmlFor="batch-id-search" className={styles.toolbarLabel}>
                                    Search by Batch ID
                                </label>
                                <input
                                    id="batch-id-search"
                                    type="text"
                                    placeholder="Filter by batch ID..."
                                    value={batchIdSearch}
                                    onChange={(e) => setBatchIdSearch(e.target.value)}
                                    className={styles.searchInput}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label htmlFor="status-filter" className={styles.toolbarLabel}>
                                    Status
                                </label>
                                <select
                                    id="status-filter"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                                    className={styles.statusSelect}
                                >
                                    <option value="all">All</option>
                                    <option value="requested">ATE Requested</option>
                                    <option value="not_requested">ATE Not Requested</option>
                                </select>
                            </div>
                            {filteredAndSortedBatches.length < allBatches.length && (
                                <span className={styles.filterSummary}>
                                    Showing {filteredAndSortedBatches.length} of {allBatches.length} batches
                                </span>
                            )}
                        </div>
                        <div className={styles.tableWrapper}>
                            <table className={styles.allocationTable}>
                                <thead>
                                    <tr>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('batch_id')}
                                            >
                                                Batch ID {sortBy === 'batch_id' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('nera_id')}
                                            >
                                                Nera ID {sortBy === 'nera_id' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('created')}
                                            >
                                                Created At {sortBy === 'created' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>Introducer</th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('clients')}
                                            >
                                                Total Clients {sortBy === 'clients' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>Book Value</th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('value')}
                                            >
                                                Draw Down Value {sortBy === 'value' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('status')}
                                            >
                                                ATE Status {sortBy === 'status' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>Submitted / API</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAndSortedBatches.map((batch) => {
                                    const ateRequested = batch.config?.ate_requested
                                    const ateRequestedDate = batch.config?.ate_requested_date
                                    const rowProps = batch.isDemo
                                        ? {}
                                        : {
                                              className: styles.clickableRow,
                                              onClick: () => handleViewDetails(batch.batch_id),
                                              role: 'button' as const,
                                              tabIndex: 0,
                                              onKeyDown: (e: React.KeyboardEvent) => {
                                                  if (e.key === 'Enter' || e.key === ' ') {
                                                      e.preventDefault()
                                                      handleViewDetails(batch.batch_id)
                                                  }
                                              },
                                          }
                                    return (
                                        <tr key={batch.batch_id} {...rowProps}>
                                            <td>{batch.batch_id || 'N/A'}</td>
                                            <td>{batch.config?.nera_id || '-'}</td>
                                            <td>{formatDateOnly(batch.allocation_date || batch.created_at || '')}</td>
                                            <td>{batch.introducer || 'N/A'}</td>
                                            <td>{batch.total_clients || 0}</td>
                                            <td>
                                                {batch.book_value
                                                    ? `£${batch.book_value.toLocaleString('en-GB', {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2,
                                                      })}`
                                                    : '£0.00'}
                                            </td>
                                            <td>
                                                {batch.draw_down_value
                                                    ? `£${batch.draw_down_value.toLocaleString('en-GB', {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2,
                                                      })}`
                                                    : '£0.00'}
                                            </td>
                                            <td>
                                                <div className={styles.ateStatusCell}>
                                                    <span
                                                        className={`${styles.ateStatusBadge} ${
                                                            ateRequested ? styles.ateRequested : styles.ateNotRequested
                                                        }`}
                                                    >
                                                        {ateRequested ? 'Requested' : 'Not Requested'}
                                                    </span>
                                                    {ateRequestedDate && (
                                                        <span className={styles.ateDate}>
                                                            {formatDate(ateRequestedDate)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={styles.submittedApiCell}>
                                                <span
                                                    className={`${styles.statusPill} ${
                                                        (batch.submitted ?? 0) > 0 ? styles.statusPillSuccess : styles.statusPillPending
                                                    }`}
                                                >
                                                    {(batch.submitted ?? 0) > 0 ? 'Submitted' : 'Not started'}
                                                </span>
                                                <span
                                                    className={`${styles.statusPill} ${
                                                        (batch.api_transferred ?? 0) > 0 ? styles.statusPillSuccess : styles.statusPillPending
                                                    }`}
                                                >
                                                    {(batch.api_transferred ?? 0) > 0 ? 'API Transferred' : 'Not started'}
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {filteredAndSortedBatches.length === 0 && (
                            <div className={styles.noResults}>
                                <p>No batches match your search or filter.</p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className={styles.noResults}>
                        <p>No funding batches found.</p>
                    </div>
                )}
            </div>
        </div>
    )
}
